import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { FilterBar, type FilterValues } from "./FilterBar";
import { InterestsDialog } from "./InterestsDialog";
import { KeywordsDialog } from "./KeywordsDialog";
import { EditProfileDialog } from "./EditProfileDialog";
import { LineAlertsCard } from "./LineAlertsCard";
import { TermColumn, type ColumnTerm } from "./TermColumn";
import { LineAnalysisPill } from "@/components/dashboard/LineAnalysisPill";
import { useInterests } from "@/hooks/queries/useInterests";
import { useProfileItems } from "@/hooks/queries/useProfileItems";
import { useUpdateInterests } from "@/hooks/mutations/useInterestMutations";
import { useUpdateProfileItems } from "@/hooks/mutations/useProfileItemMutations";
import { useUpdateSearchProfile } from "@/hooks/mutations/useSearchProfileMutations";
import { useDebouncedSave } from "@/hooks/useDebouncedSave";
import { interestKeys, profileItemKeys } from "@/lib/query-keys";
import { capture } from "@/lib/posthog";
import type { ProfileItem, SearchProfile, UserInterest } from "@/types/domain";

/** Muestra el aviso de "deshacer" de una eliminación hecha desde el panel. */
function undoToast(message: string, restore: () => void) {
  toast(message, { action: { label: "Deshacer", onClick: restore } });
}

/** Los filtros del panel, en las columnas de la línea de negocio. */
function filtersToProfileData(patch: Partial<FilterValues>): Partial<SearchProfile> {
  const data: Partial<SearchProfile> = {};
  if ("departmentIds" in patch)
    data.searchprofile_departmentids = patch.departmentIds;
  if ("provinceIds" in patch)
    data.searchprofile_provinceids = patch.provinceIds;
  if ("districtIds" in patch)
    data.searchprofile_districtids = patch.districtIds;
  if ("dateFrom" in patch)
    data.searchprofile_date_from = patch.dateFrom || null;
  if ("dateTo" in patch) data.searchprofile_date_to = patch.dateTo || null;
  if ("entityIds" in patch)
    data.searchprofile_procuringentityids = patch.entityIds;
  if ("subjects" in patch) data.searchprofile_subjects = patch.subjects;
  if ("stages" in patch) data.searchprofile_stages = patch.stages;
  if ("maxDays" in patch)
    data.searchprofile_contratadomaxdays = patch.maxDays;
  if ("onlyInterests" in patch)
    data.searchprofile_onlyinterests = patch.onlyInterests;
  // De vuelta a null: el tamaño gatea también las notificaciones (ver
  // `notifyNewMatches`), así que "Todas" tiene que quedar sin filtro y no
  // como el string "all".
  if ("scope" in patch)
    data.searchprofile_scope =
      patch.scope && patch.scope !== "all" ? patch.scope : null;
  if ("amountMin" in patch) data.searchprofile_amountmin = patch.amountMin ?? null;
  if ("amountMax" in patch) data.searchprofile_amountmax = patch.amountMax ?? null;
  return data;
}

export function ProfileCard({
  profile,
  matchedCount = 0,
  className,
}: {
  profile: SearchProfile;
  matchedCount?: number;
  className?: string;
}) {
  const profileId = profile.searchprofile_id;
  const postulates = Boolean(profile.searchprofile_checkqualification);
  const qc = useQueryClient();
  const { data: interests = [] } = useInterests(profileId);
  const { data: items = [] } = useProfileItems(profileId);
  const updateInterests = useUpdateInterests();
  const updateItems = useUpdateProfileItems();
  const update = useUpdateSearchProfile();
  const [editOpen, setEditOpen] = useState(false);
  const [interestsOpen, setInterestsOpen] = useState(false);
  const [keywordsOpen, setKeywordsOpen] = useState(false);

  // Guardar recursos e ítems recalcula todos los matches del perfil, así que
  // las ediciones sueltas del panel se agrupan antes de salir a la red.
  const { schedule: saveInterests, status: interestsStatus } =
    useDebouncedSave<UserInterest[]>((next) =>
      updateInterests.mutateAsync(
        {
          // `active` viaja siempre: el backend borra y recrea, así que omitirlo
          // reactivaría todo lo que el usuario había apagado.
          interests: next.map((i) => ({
            keyword: i.keyword,
            active: i.active ?? true,
            priceitemcategory_id: i.priceitemcategory_id,
          })),
          searchProfileId: profileId,
        },
        {
          onError: () => {
            toast.error("No se pudieron guardar los recursos");
            qc.invalidateQueries({
              queryKey: interestKeys.byProfile(profileId),
            });
          },
        },
      ),
    );

  const { schedule: saveItems, status: itemsStatus } =
    useDebouncedSave<ProfileItem[]>((next) =>
      updateItems.mutateAsync(
        { items: next, searchProfileId: profileId },
        {
          onError: () => {
            toast.error("No se pudieron guardar los ítems");
            qc.invalidateQueries({
              queryKey: profileItemKeys.byProfile(profileId),
            });
          },
        },
      ),
    );

  // Se pinta desde la caché para que el chip cambie al instante; el refetch
  // posterior al guardado confirma con lo que devuelve el backend.
  function applyInterests(next: UserInterest[]) {
    qc.setQueryData(interestKeys.byProfile(profileId), next);
    saveInterests(next);
  }

  function applyItems(next: ProfileItem[]) {
    qc.setQueryData(profileItemKeys.byProfile(profileId), next);
    saveItems(next);
  }

  const interestTerms: ColumnTerm[] = interests.map((interest, index) => ({
    id: String(index),
    term: interest.keyword,
    tag: interest.category_name,
    active: interest.active ?? true,
  }));

  const itemTerms: ColumnTerm[] = items.map((item, index) => ({
    id: String(index),
    term: item.keyword,
    active: item.active ?? true,
  }));

  function addInterest(term: string) {
    if (interests.some((i) => i.keyword === term)) return;
    capture("interest_added", { source: "panel" });
    applyInterests([
      ...interests,
      {
        keyword: term,
        active: true,
        priceitemcategory_id: null,
        category_name: null,
      },
    ]);
  }

  function removeInterest(id: string) {
    const index = Number(id);
    const removed = interests[index];
    if (!removed) return;
    applyInterests(interests.filter((_, i) => i !== index));
    undoToast(`«${removed.keyword.trim()}» eliminado`, () =>
      applyInterests(interests),
    );
  }

  function toggleInterest(id: string) {
    const index = Number(id);
    applyInterests(
      interests.map((interest, i) =>
        i === index
          ? { ...interest, active: !(interest.active ?? true) }
          : interest,
      ),
    );
  }

  function addItem(term: string) {
    if (items.some((item) => item.keyword === term)) return;
    capture("profile_item_added", { source: "panel" });
    applyItems([...items, { keyword: term, active: true }]);
  }

  function removeItem(id: string) {
    const index = Number(id);
    const removed = items[index];
    if (!removed) return;
    applyItems(items.filter((_, i) => i !== index));
    undoToast(`«${removed.keyword.trim()}» eliminado`, () => applyItems(items));
  }

  function toggleItem(id: string) {
    const index = Number(id);
    applyItems(
      items.map((item, i) =>
        i === index ? { ...item, active: !(item.active ?? true) } : item,
      ),
    );
  }

  // Los clics seguidos en los filtros (varias etapas) salen en un solo guardado:
  // cada guardado vuelve a pedir la lista y los gráficos. Mientras, se pinta el borrador.
  const [filterDraft, setFilterDraft] = useState<Partial<FilterValues>>({});
  const { schedule: saveFilters } = useDebouncedSave<Partial<FilterValues>>(
    async (draft) => {
      try {
        await update.mutateAsync({ id: profileId, data: filtersToProfileData(draft) });
      } catch {
        toast.error("No se pudieron guardar los filtros");
      }
      // Se suelta lo guardado (o lo que falló), salvo lo que volvió a cambiar mientras tanto.
      setFilterDraft((current) => {
        const next = { ...current };
        for (const key of Object.keys(draft) as (keyof FilterValues)[]) {
          if (next[key] === draft[key]) delete next[key];
        }
        return next;
      });
    },
    1000,
  );

  const savedFilterValue: FilterValues = {
    departmentIds: profile.searchprofile_departmentids ?? [],
    provinceIds: profile.searchprofile_provinceids ?? [],
    districtIds: profile.searchprofile_districtids ?? [],
    dateFrom: profile.searchprofile_date_from ?? "",
    dateTo: profile.searchprofile_date_to ?? "",
    entityIds: profile.searchprofile_procuringentityids ?? [],
    subjects: profile.searchprofile_subjects ?? [],
    stages: profile.searchprofile_stages ?? [],
    maxDays: profile.searchprofile_contratadomaxdays ?? null,
    onlyInterests: !!profile.searchprofile_onlyinterests,
    // La columna guarda null para "las dos"; el FilterBar usa el centinela "all".
    scope: profile.searchprofile_scope ?? "all",
    amountMin: profile.searchprofile_amountmin ?? null,
    amountMax: profile.searchprofile_amountmax ?? null,
  };
  const filterValue: FilterValues = { ...savedFilterValue, ...filterDraft };

  function onFilterChange(patch: Partial<FilterValues>) {
    capture("filters_changed", { filter: Object.keys(patch)[0] });
    const next = { ...filterDraft, ...patch };
    setFilterDraft(next);
    saveFilters(next);
  }

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {/* Header. En móvil no va: el panel plegable ya muestra el nombre de la
          línea y su resumen justo encima. Renombrar queda solo en escritorio. */}
      <div className="hidden items-start justify-between lg:flex">
        <div className="border-l-4 border-primary pl-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h2 className="text-xl font-black tracking-tight">
              {profile.searchprofile_name}
            </h2>
            <LineAnalysisPill lineId={profile.searchprofile_id} />
          </div>
          <p className="text-sm text-slate-400">
            Resultados de esta línea de negocio · {matchedCount} licitaciones
            coinciden con lo que vendes
          </p>
        </div>
        <Button variant="default" size="sm" onClick={() => setEditOpen(true)}>
          <Pencil className="size-3.5" />
          Editar
        </Button>
      </div>
      <div className="rounded-2xl bg-card p-4 shadow-sm lg:p-6">
        {/* Recursos / Items. Donde la empresa postula (análisis activo) se filtra por
            zona e ítems: sus recursos no matchean mientras la línea esté marcada. */}
        <div
          className={cn(
            "grid grid-cols-1 gap-5",
            !postulates && "md:grid-cols-2 md:gap-0 md:divide-x",
          )}
        >
          {!postulates && (
            <TermColumn
              title="Recursos"
              description={
                <>
                  Materiales, mano de obra, equipos y subcontratos que rastreamos{" "}
                  <strong className="font-semibold text-foreground/70">
                    dentro del presupuesto de las obras
                  </strong>
                  .
                </>
              }
              terms={interestTerms}
              emptyLabel="No has agregado recursos todavía."
              addPlaceholder="Escribe y Enter"
              status={interestsStatus}
              onAdd={addInterest}
              onRemove={removeInterest}
              onToggle={toggleInterest}
              onManage={() => setInterestsOpen(true)}
              className="md:pr-6"
            />
          )}

          <TermColumn
            title="Items"
            description={
              postulates ? (
                <>
                  Filtra las obras por su{" "}
                  <strong className="font-semibold text-foreground/70">descripción</strong>:
                  puente, pistas, saneamiento.
                </>
              ) : (
                <>
                  Filtra por la{" "}
                  <strong className="font-semibold text-foreground/70">
                    descripción de los ítems
                  </strong>{" "}
                  de la licitación. Es la mejor forma de encontrar{" "}
                  <strong className="font-semibold text-foreground/70">
                    bienes y servicios
                  </strong>
                  .
                </>
              )
            }
            terms={itemTerms}
            emptyLabel="No has agregado ítems todavía."
            addPlaceholder="Escribe y Enter"
            status={itemsStatus}
            onAdd={addItem}
            onRemove={removeItem}
            onToggle={toggleItem}
            onManage={() => setKeywordsOpen(true)}
            className={cn(!postulates && "md:pl-6")}
          />
        </div>

        <Separator className="my-5" />

        {/* Filtros */}
        <FilterBar
          value={filterValue}
          onChange={onFilterChange}
          showProvinceDistrict
          showScope
          showAmount
        />

        <Separator className="my-5" />

        <LineAlertsCard profile={profile} interests={interests} items={items} />

        <EditProfileDialog
          profile={profile}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
        <InterestsDialog
          searchProfileId={profile.searchprofile_id}
          open={interestsOpen}
          onOpenChange={setInterestsOpen}
        />
        <KeywordsDialog
          profile={profile}
          open={keywordsOpen}
          onOpenChange={setKeywordsOpen}
        />
      </div>
    </div>
  );
}
